<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { Helper } from '@/mixin/helper'
import Swal from 'sweetalert2'
import * as pinType from '@/services/rest-api-tools/lib/pin-type/pinType/'
import * as sensorType from '@/services/rest-api-tools/lib/sensor-type/sensorType/'
import * as deviceType from '@/services/rest-api-sms/lib/device-type/device-type/'
import * as alertCode from '@/services/rest-api-sms/lib/alertCode/alertCode/'
import * as checklistApi from '@/services/rest-api-sms/lib/checklist/checklist'
import * as checklistDetail from '@/services/rest-api-sms/lib/checklist/checklist-detail'
import LoadingSkeleton from './components/FormSkeleton.vue'
import Multiselect from 'vue-multiselect'
import { required } from 'vuelidate/lib/validators'
import draggable from 'vuedraggable'
import moment from 'moment'
import Confirmation from '@/components/widgets/modals/Confirmation'
import { workOrderTypes } from '../lib/workOrderTypes'
import { checklistDetailTypes } from '../lib/checklistDetailTypes'
import { mapGetters } from 'vuex'

moment.locale('id')

const toNumber = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

export default {
  page: {
    title: 'Work Order SOP Detail',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    LoadingSkeleton,
    Multiselect,
    draggable,
    Confirmation,
  },

  mixins: [Helper],

  props: {
    formErrors: {},
  },

  data() {
    return {
      isSubmit: false,
      fileAccepted: ['application/pdf', 'image/png', 'image/jpeg', 'image/gif'],
      form: {
        checklistName: null,
        checklistFlag: null,
        woTypes: [],
        deviceTypes: [{ deviceCode: null, deviceName: 'All' }],
        sensorTypes: [],
        pinTypes: [],
        alertCodes: [],
        interval: null,
        checklistDetails: [],
        relatedChecklists: [],
        isActive: false,
        isMandatory: false,
        sop_reference: null,
        is_validation_required: false,
        is_need_engine: false,
        is_need_all_pass: false,
      },
      intervalOptions: [
        { value: '00:01:00', text: '1 minute' },
        { value: '00:05:00', text: '5 minute' },
        { value: '00:10:00', text: '10 minute' },
        { value: '00:15:00', text: '15 minute' },
        { value: '00:20:00', text: '20 minute' },
        { value: '00:30:00', text: '30 minute' },
        { value: '01:00:00', text: '1 hour' },
      ],
      dropdown: {
        woTypes: [],
        deviceTypes: [],
        sensorTypes: [],
        pinTypes: [],
        alertCodes: [],
        checklistDetailTypes: [],
        relatedChecklists: [],
      },
      isDeleting: false,
      sortBy: 'sorting',
      sortDesc: false,
      loading: false,
      indeterminate: null,
      selected: [],
      selectedModel: [],
      allSelected: false,
      checklistDetail: {
        woTypes: [],
        deviceTypes: [{ deviceCode: null, deviceName: 'All' }],
        types: [],
        enums: [],
        isActive: false,
        isMandatory: false,
        interval: null,
        description: null,
        checklistDetailId: null,
        sop_reference: null,
        is_validation_required: false,
        timeout_off: null,
        timeout_on: null,
        time_ingestion: null,
        minimal_data_ingestion_off: null,
        minimal_data_ingestion_on: null,
        minimal_data_validation: null,
        difference_validation: null,
        difference_n_validation: null,
        field_qc: null,
      },
      loadingTable: false,
      modalConfirm: false,
      previewImgSrc: null,
    }
  },

  validations: {
    form: {
      woTypes: { required },
      checklistName: { required },
      checklistFlag: { required },
      deviceTypes: { required },
    },
    checklistDetail: {
      description: { required },
      types: { required },
      deviceTypes: { required },
    },
  },

  computed: {
    ...mapGetters({
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),

    title() {
      return 'Work Order SOP Detail'
    },

    isSopReferenceActive() {
      return this.getFeatureFlagData('sop_reference_active').value
    },

    items() {
      return [
        {
          text: this.$t('workOrder.title'),
          to: '/work-order',
        },
        {
          text: this.$t('workOrder.column.sop'),
          to: '/work-order/sop',
        },
        {
          text:
            this.$route.params.id === undefined
              ? this.$t('workOrder.sop.detail')
              : this.form.checklistName,
        },
      ]
    },

    woTypeOptions() {
      const dataWorkOrderTypes = workOrderTypes()
      const selectedCodes = new Set(this.form.woTypes?.map((type) => type.code))
      const woTypes = dataWorkOrderTypes.filter((option) => !selectedCodes.has(option.code))
      return woTypes
    },

    deviceTypeOptions() {
      const deviceTypes = this.dropdown.deviceTypes

      // Create the "All" option
      const allOption = { deviceCode: null, deviceName: 'All' }

      // If "All" is already selected, only return the "All" option
      if (this.form.deviceTypes && this.form.deviceTypes.some((type) => type.deviceCode === null)) {
        return [allOption]
      }

      // Otherwise, filter out already selected options and add "All" at the beginning
      const selectedCodes = new Set(this.form.deviceTypes?.map((type) => type.deviceCode))
      const dt = deviceTypes.filter((option) => !selectedCodes.has(option.deviceCode))

      return [allOption, ...dt]
    },

    sensorTypeOptions() {
      const sensorTypes = this.dropdown.sensorTypes
      const selectedCodes = new Set(this.form.sensorTypes?.map((type) => type.sensor_code))
      const st = sensorTypes.filter((option) => !selectedCodes.has(option.sensor_code))
      return st
    },

    pinTypeOptions() {
      const pinTypes = this.dropdown.pinTypes
      const selectedCodes = new Set(this.form.pinTypes?.map((type) => type.pin_code))
      const pt = pinTypes.filter((option) => !selectedCodes.has(option.pin_code))
      return pt
    },

    alertCodeOptions() {
      const alertCodes = this.dropdown.alertCodes
      const selectedCodes = new Set(this.form.alertCodes?.map((type) => type.alert_code))
      const ac = alertCodes.filter((option) => !selectedCodes.has(option.alert_code))
      return ac
    },

    checklistDetailTypeOptions() {
      const dataChecklistDetailTypes = checklistDetailTypes()
      const selectedCodes = new Set(this.checklistDetail.types?.map((type) => type !== null))
      const cdTypes = dataChecklistDetailTypes.filter((option) => !selectedCodes.has(option.code))
      return cdTypes
    },

    checklistDetailEnumOptions() {
      const enums = this.checklistDetail.enums || []
      const selectedCodes = new Set(this.checklistDetail.enums?.map((type) => type !== null))
      const en = enums.filter((option) => !selectedCodes.has(option))
      return en
    },

    checklistDetailWoTypeOptions() {
      const dataWorkOrderTypes = workOrderTypes()
      const selectedCodes = new Set(this.checklistDetail.woTypes?.map((type) => type.code))
      const woTypes = dataWorkOrderTypes.filter((option) => !selectedCodes.has(option.code))
      return woTypes
    },

    checklistDetailDeviceTypeOptions() {
      const deviceTypes = this.dropdown.deviceTypes

      // Create the "All" option
      const allOption = { deviceCode: null, deviceName: 'All' }

      // If "All" is already selected, only return the "All" option
      if (
        this.checklistDetail.deviceTypes &&
        this.checklistDetail.deviceTypes.some((type) => type.deviceCode === null)
      ) {
        return [allOption]
      }

      // Otherwise, filter out already selected options and add "All" at the beginning
      const selectedCodes = new Set(
        this.checklistDetail.deviceTypes?.map((type) => type.deviceCode)
      )
      const dt = deviceTypes.filter((option) => !selectedCodes.has(option.deviceCode))

      return [allOption, ...dt]
    },

    relatedChecklistOptions() {
      const relatedChecklistIds = this.dropdown.relatedChecklists
      const currentChecklistId = parseInt(this.$route.params.id)
      // Create Set of selected checklist IDs
      const selectedRelatedChecklistIds = new Set(
        this.form.relatedChecklists?.map((checklist) => checklist.name)
      )
      // Filter out both selected IDs and current checklist ID
      const rci = relatedChecklistIds.filter(
        (option) =>
          !selectedRelatedChecklistIds.has(option.checklist_id) &&
          option.checklist_id !== currentChecklistId
      )
      return rci
    },

    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
  },

  async created() {
    this.loading = true
    await this.getDeviceTypes()
    await this.getSensorTypes()
    await this.getPinTypes()
    await this.getAlertCodes()
    await this.getRelatedChecklists()
    await this.getSopDetail()
    this.loading = false
  },

  methods: {
    async getSopDetail() {
      const checklist = await checklistApi.getById(this.$route.params.id)
      await this.initEditPage(checklist)
    },

    async getSensorTypes() {
      const sensorTypes = await sensorType.get()
      this.dropdown.sensorTypes = sensorTypes
    },

    async getPinTypes() {
      const pinTypes = await pinType.get()
      this.dropdown.pinTypes = pinTypes
    },

    async getAlertCodes() {
      let queryParam = {
        limit: 1,
        page: 1,
      }
      let alertCodes = await alertCode.get(queryParam)
      queryParam.limit = alertCodes.metadata.total_count
      alertCodes = await alertCode.get(queryParam)
      this.dropdown.alertCodes = alertCodes.data.paginated_result
    },

    async getDeviceTypes() {
      let queryParam = {
        limit: 1,
        page: 1,
      }
      let deviceTypes = await deviceType.get(queryParam)
      queryParam.limit = deviceTypes.metadata.total_count
      deviceTypes = await deviceType.get(queryParam)
      this.dropdown.deviceTypes = deviceTypes.data.paginated_result
    },

    async getRelatedChecklists() {
      const response = await checklistApi.get()
      this.dropdown.relatedChecklists = response
    },

    async initEditPage(item) {
      // Check if device_type is null or empty array
      const deviceTypes =
        !item.device_type || item.device_type.length === 0
          ? [{ deviceCode: null, deviceName: 'All' }]
          : item.device_type

      // Ensure checklist_detail exists and is an array
      item.checklist_detail = item.checklist_detail || []

      // If checklist_detail is an array of objects
      if (Array.isArray(item.checklist_detail)) {
        item.checklist_detail.forEach((detail) => {
          if (!detail.device_type || detail.device_type === null) {
            detail.device_type = [{ deviceCode: null, deviceName: 'All' }]
          }
        })
      } else if (item.checklist_detail && typeof item.checklist_detail === 'object') {
        // If checklist_detail is a single object
        if (!item.checklist_detail.device_type || item.checklist_detail.device_type === null) {
          item.checklist_detail.device_type = [{ deviceCode: null, deviceName: 'All' }]
        }
      }

      this.form = {
        checklistName: item.name,
        checklistFlag: item.flag,
        deviceTypes: deviceTypes,
        sensorTypes: item.sensor_type,
        pinTypes: item.pin_type,
        alertCodes: item.alert_code,
        interval: item.c_interval,
        checklistDetails: item.checklist_detail,
        woTypes: item.wo_type ? this.getWorkOrderTypeObjects(item.wo_type) : [],
        relatedChecklists: item.related_checklists,
        isActive: item.is_active,
        isMandatory: item.is_mandatory,
        sop_reference: item.sop_reference,
        is_validation_required: item.is_validation_required,
        is_need_engine: item.is_need_engine,
        is_need_all_pass: item.is_need_all_pass,
      }
    },

    getWorkOrderTypeObjects(codes) {
      const dataWorkOrderTypes = workOrderTypes()
      const data = codes.map((code) => {
        const matchedType = dataWorkOrderTypes.find((type) => type.code === code)
        return matchedType
      })
      return data
    },

    getChecklistDetailTypeObjects(codes) {
      const dataChecklistDetailTypes = checklistDetailTypes()
      const data = codes.map((code) => {
        const matchedType = dataChecklistDetailTypes.find((type) => type.code === code)
        return matchedType
      })
      return data
    },

    confirmDelete() {
      this.modalConfirm = true
    },

    toCancel() {
      let isEmpty = this.checkEmpty()
      if (this.$route.params.id) {
        isEmpty = !this.checkChanged()
      }

      if (isEmpty) {
        this.$router.push('/master/fleet')
      } else {
        Swal.fire({
          title: this.$t('global.alert.text.cancel'),
          text: this.$t('global.alert.text.confirmCancel'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('global.yes'),
          cancelButtonText: this.$t('global.no'),
        }).then((result) => {
          if (result.value) {
            this.$router.push('/master/fleet')
          }
        })
      }
    },

    addChecklistDetail() {
      this.checklistDetail = {
        woTypes: [],
        deviceTypes: [],
        types: [],
        enums: [],
        isActive: false,
        isMandatory: false,
        interval: null,
        description: null,
        checklistDetailId: null,
        sop_reference: null,
        is_validation_required: false,

        timeout_off: null,
        timeout_on: null,
        time_ingestion: null,
        minimal_data_ingestion_off: null,
        minimal_data_ingestion_on: null,
        minimal_data_validation: null,
        difference_validation: null,
        difference_n_validation: null,
        field_qc: null,
      }
      this.$bvModal.show('modal-sop-detail')
    },

    getChecklistDetailTypeLabel(code) {
      const dataChecklistDetailTypes = checklistDetailTypes()
      const type = dataChecklistDetailTypes.find((item) => item.code === code)
      return type ? type.name : code
    },

    getWorkOrderTypeLabel(code) {
      const dataWorkOrderTypes = workOrderTypes()
      const type = dataWorkOrderTypes.find((item) => item.code === code)
      return type ? type.name : code
    },

    normalizeIntervalValue(interval) {
      if (!interval) return null
      if (typeof interval === 'number' || /^[0-9]+$/.test(String(interval))) {
        const minutes = Number(interval)
        const hours = Math.floor(minutes / 60)
        const mins = minutes % 60
        return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`
      }
      return interval
    },

    getIntervalLabel(interval) {
      const normalizedInterval = this.normalizeIntervalValue(interval)
      const option = this.intervalOptions.find((item) => item.value === normalizedInterval)
      return option ? option.text : normalizedInterval || '-'
    },

    toggleAll(checked) {
      this.selectedModel = []
      this.selected = []
      if (checked) {
        this.form.checklistDetails.forEach((item, index) => {
          this.selected.push(item.checklist_detail_id)
          this.selectedModel[index] = true
        })
      } else {
        this.form.checklistDetails.forEach((_, index) => {
          this.selectedModel[index] = false
        })
      }
    },

    checkTable(event, index) {
      const detailId = this.form.checklistDetails[index].checklist_detail_id
      if (event) {
        this.selected.push(detailId)
      } else {
        const myIndex = this.selected.indexOf(detailId)
        if (myIndex !== -1) {
          this.selected.splice(myIndex, 1)
        }
      }
    },

    onRowClicked(item) {
      this.checklistDetail = {
        description: item.description,
        types: item.checklist_detail_type
          ? this.getChecklistDetailTypeObjects(item.checklist_detail_type)
          : [],
        woTypes: item.wo_type ? this.getWorkOrderTypeObjects(item.wo_type) : [],
        deviceTypes: item.device_type,
        enums: item.enum || [],
        isActive: item.is_active,
        isMandatory: item.is_mandatory,
        interval: item.c_interval,
        checklistDetailId: item.checklist_detail_id,
        sop_reference: item.sop_reference,
        is_validation_required: item.is_validation_required,

        timeout_off: toNumber(item.timeout_off),
        timeout_on: toNumber(item.timeout_on),
        time_ingestion: toNumber(item.time_ingestion),
        minimal_data_ingestion_off: toNumber(item.minimal_data_ingestion_off),
        minimal_data_ingestion_on: toNumber(item.minimal_data_ingestion_on),
        minimal_data_validation: toNumber(item.minimal_data_validation),
        difference_validation: toNumber(item.difference_validation),
        difference_n_validation: toNumber(item.difference_n_validation),
        field_qc: item.field_qc,
      }
      this.$bvModal.show('modal-sop-detail')
    },

    async addSelectionEnumTag(newValue) {
      this.checklistDetail.enums.push(newValue)
    },

    async handleSopFormFileChange(event) {
      const files = event?.target?.files
      const file = files && files[0] ? files[0] : null
      if (!file) return

      if (!this.fileAccepted.includes(file.type)) {
        this.$toast.error('Unggah gagal. Format file tidak sesuai.')
        event.target.value = null
        return
      }

      const MAX_FILE_SIZE = 1024 * 1024 * 2 // 2MB
      if (file.size > MAX_FILE_SIZE) {
        this.$toast.error('Unggah gagal. Ukuran file melebihi batas ketentuan.')
        event.target.value = null
        return
      }

      const toBase64 = (f) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(f)
          reader.onload = () => resolve(reader.result)
          reader.onerror = (error) => reject(error)
        })

      const convertedBase64 = await toBase64(file)
      const base64 = String(convertedBase64)
      const sizeMB = (file.size / 1024 / 1024).toFixed(2)

      this.form = {
        ...this.form,
        sop_reference: { name: file.name, base64, sizeMB },
      }
    },

    async handleSopChecklistFileChange(event) {
      const files = event?.target?.files
      const file = files && files[0] ? files[0] : null
      if (!file) return

      if (!this.fileAccepted.includes(file.type)) {
        this.$toast.error('Unggah gagal. Format file tidak sesuai.')
        event.target.value = null
        return
      }

      const MAX_FILE_SIZE = 1024 * 1024 * 2 // 2MB
      if (file.size > MAX_FILE_SIZE) {
        this.$toast.error('Unggah gagal. Ukuran file melebihi batas ketentuan.')
        event.target.value = null
        return
      }

      const toBase64 = (f) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(f)
          reader.onload = () => resolve(reader.result)
          reader.onerror = (error) => reject(error)
        })

      const convertedBase64 = await toBase64(file)
      const base64 = String(convertedBase64)
      const sizeMB = (file.size / 1024 / 1024).toFixed(2)

      this.checklistDetail = {
        ...this.checklistDetail,
        sop_reference: { name: file.name, base64, sizeMB },
      }
    },

    async handleSave() {
      this.$v.form.$touch()
      const deviceCodes = Array.isArray(this.form.deviceTypes)
        ? (() => {
            const result = this.form.deviceTypes
              .map((item) => item?.deviceCode)
              .filter((v) => Number.isInteger(v))
            return result.length > 0 ? result : null
          })()
        : null
      const payload = {
        name: this.form.checklistName,
        flag: this.form.checklistFlag,
        wo_types: this.form.woTypes?.map((item) => item.code) || [],
        device_codes: deviceCodes,
        sensor_codes: this.form.sensorTypes?.map((item) => item.sensor_code) || [],
        alert_codes: this.form.alertCodes?.map((item) => item.alert_code) || [],
        pin_codes: this.form.pinTypes?.map((item) => item.pin_code) || [],
        related_checklist_ids: this.form.relatedChecklists?.map((item) => item.checklist_id) || [],
        c_interval: this.form.interval,
        is_active: this.form.isActive,
        is_mandatory: this.form.isMandatory,
        updated_at: moment(new Date()).utc(),
        is_validation_required: this.form.is_validation_required,
        sop_reference: this.form.sop_reference?.base64 || this.form.sop_reference,

        is_need_engine: this.form.is_need_engine,
        is_need_all_pass: this.form.is_need_all_pass,
      }
      const response = await checklistApi.update(this.$route.params.id, payload)
      if (response.status === 200) {
        this.$mcToast.success(`Data successfully updated`)
      }
    },

    async handleCreate() {
      this.loadingTable = true

      this.$v.checklistDetail.$touch()
      if (this.$v.checklistDetail.$error) {
        this.loadingTable = false
        return
      }

      const now = moment(new Date()).utc().format('YYYY-MM-DD HH:mm:ss')
      const rawDeviceTypes = this.checklistDetail.deviceTypes

      let deviceCodes = null

      if (Array.isArray(rawDeviceTypes)) {
        const filtered = rawDeviceTypes
          .map((t) => t?.deviceCode)
          .filter((code) => Number.isInteger(code))

        deviceCodes = filtered.length > 0 ? filtered : null
      }

      const payload = {
        description: this.checklistDetail.description,
        device_codes: deviceCodes,
        is_active: this.checklistDetail.isActive,
        is_mandatory: this.checklistDetail.isMandatory,
        checklist_detail_types: this.checklistDetail.types?.map((type) => type.code) || [],
        enums: this.checklistDetail.enums,
        wo_types: this.checklistDetail.woTypes?.map((type) => type.code) || [],
        created_at: now,
        updated_at: now,
        checklist_id: parseInt(this.$route.params.id),
        sorting: (this.form.checklistDetails?.length || 0) + 1,
        c_interval: this.checklistDetail.interval,
        sop_reference:
          this.checklistDetail.sop_reference?.base64 || this.checklistDetail.sop_reference,
        is_validation_required: this.checklistDetail.is_validation_required,

        timeout_off: toNumber(this.checklistDetail.timeout_off),
        timeout_on: toNumber(this.checklistDetail.timeout_on),
        time_ingestion: toNumber(this.checklistDetail.time_ingestion),
        minimal_data_ingestion_off: toNumber(this.checklistDetail.minimal_data_ingestion_off),
        minimal_data_ingestion_on: toNumber(this.checklistDetail.minimal_data_ingestion_on),
        minimal_data_validation: toNumber(this.checklistDetail.minimal_data_validation),
        difference_validation: toNumber(this.checklistDetail.difference_validation),
        difference_n_validation: toNumber(this.checklistDetail.difference_n_validation),
        field_qc: this.checklistDetail.field_qc,
      }

      const response = await checklistDetail.create(payload)
      if (response.status === 201) {
        this.getSopDetail()
        this.$mcToast.success(`Data sop detail successfully created`)
        this.loadingTable = false
        return true // Return true jika berhasil
      } else {
        this.$mcToast.error(`Data sop detail failed to create`)
      }
      this.loadingTable = false
      return false // Return false jika gagal
    },

    async handleUpdate() {
      this.loadingTable = true

      this.$v.checklistDetail.$touch()
      if (this.$v.checklistDetail.$error) {
        this.loadingTable = false
        return
      }

      const index = this.form.checklistDetails.findIndex(
        (item) => item.checklist_detail_id === this.checklistDetail.checklistDetailId
      )
      if (index !== -1) {
        const rawDeviceTypes = this.checklistDetail.deviceTypes

        let deviceCodes = null

        if (Array.isArray(rawDeviceTypes)) {
          const filtered = rawDeviceTypes
            .map((t) => t?.deviceCode)
            .filter((code) => Number.isInteger(code))

          deviceCodes = filtered.length > 0 ? filtered : null
        }

        const payload = {
          description: this.checklistDetail.description,
          device_codes: deviceCodes,
          is_active: this.checklistDetail.isActive,
          is_mandatory: this.checklistDetail.isMandatory,
          checklist_detail_types: this.checklistDetail.types?.map((type) => type.code) || [],
          enums: this.checklistDetail.enums,
          wo_types: this.checklistDetail.woTypes?.map((type) => type.code) || [],
          updated_at: moment(new Date()).utc(),
          c_interval: this.checklistDetail.interval,
          sop_reference:
            this.checklistDetail.sop_reference?.base64 || this.checklistDetail.sop_reference,
          is_validation_required: this.checklistDetail.is_validation_required,

          timeout_off: toNumber(this.checklistDetail.timeout_off),
          timeout_on: toNumber(this.checklistDetail.timeout_on),
          time_ingestion: toNumber(this.checklistDetail.time_ingestion),
          minimal_data_ingestion_off: toNumber(this.checklistDetail.minimal_data_ingestion_off),
          minimal_data_ingestion_on: toNumber(this.checklistDetail.minimal_data_ingestion_on),
          minimal_data_validation: toNumber(this.checklistDetail.minimal_data_validation),
          difference_validation: toNumber(this.checklistDetail.difference_validation),
          difference_n_validation: toNumber(this.checklistDetail.difference_n_validation),
          field_qc: this.checklistDetail.field_qc,
        }
        const data = await checklistDetail.update(this.checklistDetail.checklistDetailId, payload)
        if (data.status === 200) {
          this.$mcToast.success(`Data sop detail successfully updated`)
          const updatedDetail = {
            ...this.form.checklistDetails[index],
            description: this.checklistDetail.description,
            device_type: this.checklistDetail.deviceTypes?.length
              ? this.checklistDetail.deviceTypes
              : [{ deviceCode: null, deviceName: 'All' }],
            is_active: this.checklistDetail.isActive,
            is_mandatory: this.checklistDetail.isMandatory,
            checklist_detail_type: this.checklistDetail.types?.map((type) => type.code) || [],
            enum: this.checklistDetail.enums,
            wo_type: this.checklistDetail.woTypes?.map((type) => type.code) || [],
            c_interval: this.checklistDetail.interval,
            sop_reference: this.checklistDetail.sop_reference,
            is_validation_required: this.checklistDetail.is_validation_required,

            timeout_off: toNumber(this.checklistDetail.timeout_off),
            timeout_on: toNumber(this.checklistDetail.timeout_on),
            time_ingestion: toNumber(this.checklistDetail.time_ingestion),
            minimal_data_ingestion_off: toNumber(this.checklistDetail.minimal_data_ingestion_off),
            minimal_data_ingestion_on: toNumber(this.checklistDetail.minimal_data_ingestion_on),
            minimal_data_validation: toNumber(this.checklistDetail.minimal_data_validation),
            difference_validation: toNumber(this.checklistDetail.difference_validation),
            difference_n_validation: toNumber(this.checklistDetail.difference_n_validation),
            field_qc: this.checklistDetail.field_qc,
          }
          this.$set(this.form.checklistDetails, index, updatedDetail)
          this.loadingTable = false
          return true // Return true jika
        }
      }
      this.loadingTable = false
      return false // Return false jika gagal
    },

    async handleDragEnd() {
      this.loadingTable = true
      // Map new sorting order
      this.form.checklistDetails = this.form.checklistDetails.map((item, index) => ({
        ...item,
        sorting: index + 1,
      }))

      /// Buat payload dengan semua item dan sorting baru mereka
      const payload = {
        checklist_details: this.form.checklistDetails.map((item, index) => ({
          checklist_detail_id: item.checklist_detail_id,
          sorting: index + 1,
        })),
      }

      const response = await checklistDetail.updateMany(payload)
      if (response.status === 200) {
        this.$mcToast.success(`Data sop detail successfully updated`)
      }
      this.loadingTable = false
    },

    clearDelete() {
      this.allSelected = false
      this.selectedModel = []
      this.selected = []
    },

    async toDelete() {
      this.isDeleting = true
      this.loadingTable = true

      const payload = {
        ids: this.selected,
      }

      const response = await checklistDetail.remove(payload)
      if (!response?.data || response.status !== 200) {
        this.isDeleting = false
        this.loadingTable = false
        return
      }

      await this.getSopDetail()

      this.$mcToast.success(`Data successfully ${response.data}`)
      this.clearDelete()

      this.isDeleting = false
      this.loadingTable = false
    },

    handleBack() {
      this.$router.back()
    },

    handleFailedValidation() {
      return
    },

    async validateAndSubmit() {
      this.$v.checklistDetail.$touch()
      if (this.$v.checklistDetail.$invalid) {
        return
      }

      let success = false

      if (this.checklistDetail.checklistDetailId) {
        success = await this.handleUpdate()
      } else {
        success = await this.handleCreate()
      }

      if (success) {
        this.$bvModal.hide('modal-sop-detail')
      }
    },

    // Add this method to handle selection changes
    handleDeviceTypeSelection(selectedTypes) {
      if (!selectedTypes || selectedTypes.length === 0) {
        this.form.deviceTypes = []
        return
      }

      // Check if "All" was just selected
      const allOptionSelected = selectedTypes.some((type) => type.deviceCode === null)
      const hadAllOptionBefore = this.form.deviceTypes.some((type) => type.deviceCode === null)

      if (allOptionSelected && !hadAllOptionBefore) {
        // If "All" was just selected, set it as the only selection
        this.form.deviceTypes = [{ deviceCode: null, deviceName: 'All' }]
      } else if (!allOptionSelected && hadAllOptionBefore) {
        // If "All" was just deselected, clear the selection
        this.form.deviceTypes = []
      } else if (allOptionSelected) {
        // If "All" is still selected, keep only "All"
        this.form.deviceTypes = [{ deviceCode: null, deviceName: 'All' }]
      } else {
        // Otherwise, update with the new selection (excluding "All")
        this.form.deviceTypes = selectedTypes.filter((type) => type.deviceCode !== null)
      }
    },

    // Add this method to handle selection changes
    handleChecklistDetailDeviceTypeSelection(selectedTypes) {
      if (!selectedTypes || selectedTypes.length === 0) {
        this.checklistDetail.deviceTypes = []
        return
      }

      // Check if "All" was just selected
      const allOptionSelected = selectedTypes.some((type) => type.deviceCode === null)
      const hadAllOptionBefore = this.checklistDetail.deviceTypes.some(
        (type) => type.deviceCode === null
      )

      if (allOptionSelected && !hadAllOptionBefore) {
        // If "All" was just selected, set it as the only selection
        this.checklistDetail.deviceTypes = [{ deviceCode: null, deviceName: 'All' }]
      } else if (!allOptionSelected && hadAllOptionBefore) {
        // If "All" was just deselected, clear the selection
        this.checklistDetail.deviceTypes = []
      } else if (allOptionSelected) {
        // If "All" is still selected, keep only "All"
        this.checklistDetail.deviceTypes = [{ deviceCode: null, deviceName: 'All' }]
      } else {
        // Otherwise, update with the new selection (excluding "All")
        this.checklistDetail.deviceTypes = selectedTypes.filter((type) => type.deviceCode !== null)
      }
    },

    showSopReference(item) {
      const base64 = item?.sop_reference?.base64 || item?.sop_reference || null
      if (!base64) {
        this.$mcToast?.error && this.$mcToast.error('No SOP reference available')
        return
      }
      this.previewImgSrc = base64
      this.$bvModal.show('modal-sop-preview')
    },
  },
}
</script>

<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :items="items" class="mc-page-three" />
    </div>

    <b-skeleton-wrapper :loading="$route.params.id && loading">
      <template #loading>
        <LoadingSkeleton />
      </template>
      <FormulateForm ref="form" v-model="form" class="mc-form">
        <div class="clearfix mc-btn-group__list">
          <div class="float-end">
            <b-button
              @click="handleBack"
              variant="outline-primary"
              menu-class="dropdown-menu-end"
              class="ms-3"
            >
              <i class="bx bx-arrow-back"></i> {{ $t('global.back') }}
            </b-button>
            <b-button
              v-if="hasAccessAll(['sms-sop:update'])"
              @click="handleSave()"
              variant="primary"
              menu-class="dropdown-menu-end"
              class="ms-3"
            >
              <i class="mc-fa icon-outline_save" /> {{ $t('global.save') }}
            </b-button>
          </div>
        </div>

        <h4 class="card-title">
          {{ form.checklistName }}
        </h4>

        <b-card>
          <b-row>
            <b-col sm="12" md="3">
              <FormulateInput
                id="checklistName"
                name="checklistName"
                validation="required:trim"
                :placeholder="$t('workOrder.sop.checklistName')"
                :validation-messages="{
                  required: `${$t('workOrder.sop.checklistName')} ${$t(
                    'global.validation.required'
                  )}`,
                }"
                validation-behavior="live"
                error-behavior="live"
              >
                <template #label>
                  <div class="d-flex justify-content-between align-items-center">
                    <label class="teal-300">
                      {{ $t('workOrder.sop.checklistName') }}<i class="text-danger">*</i>
                    </label>
                  </div>
                </template>
              </FormulateInput>
            </b-col>
            <b-col sm="12" md="3">
              <div class="mb-3">
                <label>{{ $t('workOrder.sop.relatedChecklists') }}</label>
                <multiselect
                  v-model="form.relatedChecklists"
                  :options="relatedChecklistOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :hide-selected="true"
                  :allow-empty="true"
                  placeholder="Select related SOP"
                  label="name"
                  track-by="checklist_id"
                />
              </div>
            </b-col>
            <b-col sm="12" md="3">
              <div class="mb-3">
                <label class="required">{{ $t('workOrder.sop.woType') }}</label>
                <multiselect
                  v-model="form.woTypes"
                  :options="woTypeOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :hide-selected="true"
                  :allow-empty="true"
                  placeholder="Select work order types"
                  label="name"
                  track-by="code"
                  :class="{
                    'is-invalid': $v.form.woTypes.$error,
                  }"
                />
                <div v-if="$v.form.woTypes.$error" class="invalid-feedback mt-0">
                  <span v-if="!$v.form.woTypes.required">
                    {{ $t('workOrder.sop.woType') }} Wajib Terisi
                  </span>
                </div>
              </div>
            </b-col>
            <b-col sm="12" md="3">
              <FormulateInput
                id="checklistFlag"
                name="checklistFlag"
                validation="required:trim"
                :placeholder="$t('workOrder.sop.checklistFlag')"
                :validation-messages="{
                  required: `${$t('workOrder.sop.checklistFlag')} ${$t(
                    'global.validation.required'
                  )}`,
                }"
                validation-behavior="live"
                error-behavior="live"
              >
                <template #label>
                  <div class="d-flex justify-content-between align-items-center">
                    <label class="teal-300">
                      {{ $t('workOrder.sop.checklistFlag') }}<i class="text-danger">*</i>
                    </label>
                  </div>
                </template>
              </FormulateInput>
            </b-col>
            <b-col sm="12" md="3">
              <div class="mb-3">
                <label class="required">{{ $t('workOrder.sop.deviceType') }}</label>
                <multiselect
                  v-model="form.deviceTypes"
                  @input="handleDeviceTypeSelection"
                  :options="deviceTypeOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :hide-selected="true"
                  :allow-empty="true"
                  placeholder="Select device types"
                  label="deviceName"
                  track-by="deviceCode"
                  :class="{
                    'is-invalid': $v.form.deviceTypes.$error,
                  }"
                />
                <div v-if="$v.form.deviceTypes.$error" class="invalid-feedback mt-0">
                  <span v-if="!$v.form.deviceTypes.required">
                    {{ $t('workOrder.sop.deviceType') }} Wajib Terisi
                  </span>
                </div>
              </div>
            </b-col>
            <b-col sm="12" md="3">
              <div class="mb-3">
                <label>{{ $t('workOrder.sop.sensorType') }}</label>
                <multiselect
                  v-model="form.sensorTypes"
                  :options="sensorTypeOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :hide-selected="true"
                  :allow-empty="true"
                  placeholder="Select sensor types"
                  label="sensor_name"
                  track-by="sensor_code"
                />
              </div>
            </b-col>
            <b-col sm="12" md="3">
              <div class="mb-3">
                <label>{{ $t('workOrder.sop.pinType') }}</label>
                <multiselect
                  v-model="form.pinTypes"
                  :options="pinTypeOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :hide-selected="true"
                  :allow-empty="true"
                  placeholder="Select pin types"
                  label="pin_name"
                  track-by="pin_code"
                />
              </div>
            </b-col>
            <b-col sm="12" md="3">
              <div class="mb-3">
                <label>{{ $t('workOrder.sop.alertCode') }}</label>
                <multiselect
                  v-model="form.alertCodes"
                  :options="alertCodeOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :hide-selected="true"
                  :allow-empty="true"
                  placeholder="Select alert codes"
                  label="description_idn"
                  track-by="alert_code"
                />
              </div>
            </b-col>
            <b-col sm="12" md="3">
              <div class="mb-3">
                <label>{{ $t('workOrder.sop.interval') }}</label>
                <v-select
                  v-model="form.interval"
                  :options="intervalOptions"
                  label="text"
                  :reduce="(option) => option.value"
                  placeholder="Select interval"
                />
              </div>
            </b-col>
            <b-col sm="12">
              <div class="mt-3">
                <div class="form-check-right form-switch">
                  <input
                    id="SwitchCheckSizemd"
                    v-model="form.isMandatory"
                    class="form-check-input form-check-input-lg ms-2"
                    type="checkbox"
                  />
                  <label class="" for="SwitchCheckSizemd">
                    {{ $t('workOrder.sop.isMandatory') }}
                  </label>
                </div>
              </div>
            </b-col>
            <b-col sm="12">
              <div class="mt-3">
                <div class="form-check-right form-switch">
                  <input
                    id="SwitchCheckSizemd"
                    v-model="form.isActive"
                    class="form-check-input form-check-input-lg ms-2"
                    type="checkbox"
                  />
                  <label class="" for="SwitchCheckSizemd">
                    {{ $t('workOrder.sop.isActive') }}
                  </label>
                </div>
              </div>
            </b-col>
            <b-col sm="12">
              <div class="mt-3">
                <div class="form-check-right form-switch">
                  <input
                    id="SwitchCheckValidation"
                    v-model="form.is_validation_required"
                    class="form-check-input form-check-input-lg ms-2"
                    type="checkbox"
                  />
                  <label for="SwitchCheckValidation">
                    {{ $t('workOrder.sop.isValidationRequired') }}
                  </label>
                </div>
              </div>
            </b-col>
            <b-col sm="12">
              <div class="mt-3">
                <div class="form-check-right form-switch">
                  <input
                    id="SwitchNeedEngine"
                    v-model="form.is_need_engine"
                    class="form-check-input form-check-input-lg ms-2"
                    type="checkbox"
                  />
                  <label for="SwitchNeedEngine">
                    {{ $t('workOrder.sop.isNeedEngine') || 'Need Engine' }}
                  </label>
                </div>
              </div>
            </b-col>
            <b-col sm="12">
              <div class="mt-3">
                <div class="form-check-right form-switch">
                  <input
                    id="SwitchNeedAllPass"
                    v-model="form.is_need_all_pass"
                    class="form-check-input form-check-input-lg ms-2"
                    type="checkbox"
                  />
                  <label for="SwitchNeedAllPass">
                    {{ $t('workOrder.sop.isNeedAllPass') || 'Need All Pass' }}
                  </label>
                </div>
              </div>
            </b-col>
            <b-col v-if="isSopReferenceActive" sm="12">
              <div class="mt-3">
                <label>Sop Reference</label>
                <div class="mb-2 d-flex align-items-center gap-2">
                  <b-button
                    variant="outline-primary"
                    size="lg"
                    @click="$refs.nameSopFileInput.click()"
                  >
                    <i class="icon-fill_plus" />
                  </b-button>
                  <span>{{
                    form.sop_reference
                      ? form.sop_reference.name
                        ? form.sop_reference.name
                        : 'Change'
                      : 'No file selected'
                  }}</span>
                  <input
                    ref="nameSopFileInput"
                    type="file"
                    class="d-none"
                    @change="handleSopFormFileChange"
                  />
                </div>
                <div class="d-flex">
                  <b-button
                    class="m-1"
                    size="sm"
                    :variant="`${form.sop_reference ? 'primary' : 'outline-light'} small`"
                    :disabled="!form.sop_reference"
                    @click="showSopReference(form)"
                  >
                    <i class="mc-fa icon-outline_eye"></i>
                    View
                  </b-button>
                  <b-button
                    class="m-1"
                    size="sm"
                    :variant="`${form.sop_reference ? 'outline-danger' : 'outline-light'} small`"
                    :disabled="!form.sop_reference"
                    @click="
                      () => {
                        form.sop_reference = null
                      }
                    "
                  >
                    Delete
                  </b-button>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <h4 class="card-title">
          {{ $t('workOrder.sop.detail') }}
        </h4>

        <b-card>
          <div class="card-body">
            <div class="clearfix">
              <div class="float-start">
                <h6 v-if="selected.length && hasAccessAll(['sms-sop:delete'])" class="mc-row-check">
                  <span class="badge btn-success">{{ selected.length }}</span>
                  Baris terpilih.
                  <a class="color-primary" @click="clearDelete">Bersihkan semua</a>
                </h6>
              </div>

              <!-- Search -->
              <div class="float-end">
                <div class="mc-table-view">
                  <div class="dataTables_filter d-flex align-items-center justify-content-end">
                    <div>
                      <b-button
                        v-if="selected.length && hasAccessAll(['sms-sop:delete'])"
                        :disabled="isDeleting"
                        @click="confirmDelete"
                        variant="outline-danger"
                        class="me-2"
                      >
                        <i class="bx bx-trash-alt"></i> {{ $t('global.delete') }}
                      </b-button>
                    </div>
                    <div>
                      <a
                        class="btn btn-primary"
                        href="javascript: void(0);"
                        @click="addChecklistDetail"
                      >
                        <i v-if="hasAccessAll(['sms-sop:create'])" class="bx bx-plus"></i>
                        {{ $t('global.addField') }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <!-- End search -->
            </div>

            <div class="table-responsive my-3 border border-1 table-container">
              <b-overlay :show="loadingTable" rounded="sm">
                <table class="table">
                  <thead>
                    <tr>
                      <th>
                        <div class="form-check">
                          <b-form-checkbox
                            @change="toggleAll"
                            v-model="allSelected"
                            :indeterminate="indeterminate"
                            :disabled="isDeleting"
                          />
                        </div>
                      </th>
                      <th v-if="hasAccessAll(['sms-sop:update'])"></th>
                      <!-- For drag handle -->
                      <th>{{ $t('workOrder.sop.detailDescription') }}</th>
                      <th>{{ $t('workOrder.sop.isActive') }}</th>
                      <th>{{ $t('workOrder.sop.checklistDetailType') }}</th>
                      <th>{{ $t('workOrder.sop.woType') }}</th>
                      <th>{{ $t('workOrder.sop.detailEnum') }}</th>
                      <th>{{ $t('workOrder.sop.deviceType') }}</th>
                      <th>{{ $t('workOrder.sop.interval') }}</th>
                      <th>{{ $t('workOrder.sop.isMandatory') }}</th>
                      <th>
                        {{ $t('workOrder.sop.isValidationRequired') || 'Validation Required' }}
                      </th>
                      <th v-if="isSopReferenceActive">{{ $t('workOrder.sop.sopReference') }}</th>
                      <th>Timeout Off</th>
                      <th>Timeout On</th>
                      <th>Time Ingestion</th>
                      <th>Min Data Ingestion Off</th>
                      <th>Min Data Ingestion On</th>
                      <th>Min Data Validation</th>
                      <th>Difference Validation</th>
                      <th>Difference N Validation</th>
                      <th>Field QC</th>
                    </tr>
                  </thead>
                  <tbody v-if="!form.checklistDetails || form.checklistDetails.length === 0">
                    <tr>
                      <td colspan="13" class="text-center py-4">
                        <div class="empty-state">
                          <i class="bx bx-folder-open fs-1 text-muted"></i>
                          <p class="mt-2">No data available</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <draggable
                    v-model="form.checklistDetails"
                    tag="tbody"
                    handle=".drag-handle"
                    @end="handleDragEnd"
                  >
                    <tr
                      v-for="(item, index) in form.checklistDetails"
                      :key="item.checklist_detail_id"
                    >
                      <td>
                        <div class="form-check">
                          <b-form-checkbox
                            v-model="selectedModel[index]"
                            :value="true"
                            :unchecked-value="false"
                            :disabled="isDeleting"
                            @change="checkTable($event, index)"
                          />
                        </div>
                      </td>
                      <td v-if="hasAccessAll(['sms-sop:update'])">
                        <i class="fas fa-grip-vertical drag-handle cursor-move"></i>
                      </td>
                      <td>
                        <a @click="onRowClicked(item)">
                          <strong class="text-primary">{{ item.description }}</strong>
                        </a>
                      </td>
                      <td>
                        <input type="checkbox" v-model="item.is_active" :disabled="true" />
                      </td>
                      <td>
                        <div class="checklist-detail-type-tags">
                          <b-badge
                            v-for="tag in item.checklist_detail_type"
                            :key="tag"
                            class="bg-primary text-white ms-2"
                          >
                            {{ getChecklistDetailTypeLabel(tag) }}
                          </b-badge>
                        </div>
                      </td>
                      <td>
                        <div class="wo-type-tags">
                          <b-badge
                            v-for="tag in item.wo_type"
                            :key="tag"
                            class="bg-primary text-white ms-2"
                          >
                            {{ getWorkOrderTypeLabel(tag) }}
                          </b-badge>
                        </div>
                      </td>
                      <td>
                        <div class="enums-tags">
                          <b-badge
                            v-for="tag in item.enum"
                            :key="tag"
                            class="bg-primary text-white ms-2"
                          >
                            {{ tag.replace(/"/g, '') }}
                          </b-badge>
                        </div>
                      </td>
                      <td>
                        <div class="device-codes-tags">
                          <b-badge
                            v-for="tag in item.device_type"
                            :key="tag.deviceCode"
                            class="bg-primary text-white ms-2"
                          >
                            {{ tag.deviceName }}
                          </b-badge>
                        </div>
                      </td>
                      <td>{{ getIntervalLabel(item.c_interval) }}</td>
                      <td>
                        <input type="checkbox" v-model="item.is_mandatory" :disabled="true" />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          v-model="item.is_validation_required"
                          :disabled="true"
                        />
                      </td>
                      <td v-if="isSopReferenceActive">
                        <b-button
                          size="sm"
                          :variant="`${
                            item.sop_reference ? 'outline-primary' : 'outline-light'
                          } small`"
                          :disabled="!item.sop_reference"
                          @click="showSopReference(item)"
                        >
                          View
                        </b-button>
                      </td>
                      <td>{{ item.timeout_off ?? '-' }}</td>
                      <td>{{ item.timeout_on ?? '-' }}</td>
                      <td>{{ item.time_ingestion ?? '-' }}</td>
                      <td>{{ item.minimal_data_ingestion_off ?? '-' }}</td>
                      <td>{{ item.minimal_data_ingestion_on ?? '-' }}</td>
                      <td>{{ item.minimal_data_validation ?? '-' }}</td>
                      <td>{{ item.difference_validation ?? '-' }}</td>
                      <td>{{ item.difference_n_validation ?? '-' }}</td>
                      <td>{{ item.field_qc ?? '-' }}</td>
                    </tr>
                  </draggable>
                </table>
              </b-overlay>
            </div>
          </div>
        </b-card>
      </FormulateForm>
    </b-skeleton-wrapper>

    <b-modal
      id="modal-sop-detail"
      centered
      :title="`${$t('workOrder.sop.detail')} : ${checklistDetail?.description || '-'}`"
      hideHeaderClose
      :okTitle="$t('global.save')"
      okVariant="primary"
      :errors="formErrors"
      @failed-validation="handleFailedValidation"
    >
      <FormulateForm ref="form" v-model="checklistDetail" class="mc-form">
        <b-card>
          <b-row>
            <b-col sm="12" md="12">
              <FormulateInput
                id="description"
                name="description"
                :label="$t('workOrder.sop.detailDescription')"
                :placeholder="$t('workOrder.sop.detailDescription')"
                :validation="['required']"
                :validation-messages="{
                  required: `${this.$t('workOrder.sop.detailDescription')} Wajib Terisi`,
                }"
                validation-behavior="live"
                error-behavior="live"
              >
              </FormulateInput>
            </b-col>
            <b-col sm="12">
              <div class="mt-3 mb-3">
                <div class="form-check-right form-switch">
                  <input
                    id="SwitchCheckSizemd"
                    v-model="checklistDetail.isActive"
                    class="form-check-input ms-2"
                    type="checkbox"
                  />
                  <label class="" for="SwitchCheckSizemd">
                    {{ $t('workOrder.sop.isActive') }}
                  </label>
                </div>
              </div>
            </b-col>
            <b-col sm="12" md="12">
              <div class="mb-3">
                <label class="required">{{ $t('workOrder.sop.checklistDetailType') }}</label>
                <multiselect
                  v-model="checklistDetail.types"
                  :options="checklistDetailTypeOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :hide-selected="true"
                  :allow-empty="true"
                  placeholder="Select checklist detail types"
                  label="name"
                  track-by="code"
                  :class="{
                    'is-invalid': $v.checklistDetail.types.$error,
                  }"
                />
                <div v-if="$v.checklistDetail.types.$error" class="invalid-feedback mt-0">
                  <span v-if="!$v.checklistDetail.types.required">
                    {{ $t('workOrder.sop.checklistDetailType') }} Wajib Terisi
                  </span>
                </div>
              </div>
            </b-col>
            <b-col sm="12" md="12">
              <div class="mb-3">
                <label>{{ $t('workOrder.sop.woType') }}</label>
                <multiselect
                  v-model="checklistDetail.woTypes"
                  :options="checklistDetailWoTypeOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :hide-selected="true"
                  :allow-empty="true"
                  placeholder="Select checklist detail wo types"
                  label="name"
                  track-by="code"
                />
              </div>
            </b-col>
            <b-col sm="12" md="12">
              <div class="mb-3">
                <label>{{ $t('workOrder.sop.detailEnum') }}</label>
                <multiselect
                  v-model="checklistDetail.enums"
                  :options="checklistDetailEnumOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :hide-selected="true"
                  :allow-empty="true"
                  :taggable="true"
                  @tag="addSelectionEnumTag"
                  placeholder="Select checklist detail enums"
                />
                <div
                  v-if="checklistDetail.enums && checklistDetail.enums.length > 0"
                  class="enum-draggable-container"
                >
                  <small class="text-muted d-block mb-2">
                    <i class="fas fa-info-circle"></i> Drag to reorder
                  </small>
                  <draggable
                    v-model="checklistDetail.enums"
                    class="enum-tags-wrapper"
                    handle=".drag-handle-enum"
                    ghost-class="ghost-tag"
                    animation="200"
                  >
                    <span
                      v-for="(tag, index) in checklistDetail.enums"
                      :key="`enum-${index}`"
                      class="enum-tag-item"
                    >
                      <i class="fas fa-grip-vertical drag-handle-enum"></i>
                      <span class="enum-tag-text">{{ tag }}</span>
                      <i
                        class="bx bx-x enum-tag-remove"
                        @click="checklistDetail.enums.splice(index, 1)"
                      ></i>
                    </span>
                  </draggable>
                </div>
              </div>
            </b-col>
            <b-col sm="12" md="12">
              <div class="mb-3">
                <label>{{ $t('workOrder.sop.interval') }}</label>
                <v-select
                  v-model="checklistDetail.interval"
                  :options="intervalOptions"
                  label="text"
                  :reduce="(option) => option.value"
                  placeholder="Select interval"
                />
              </div>
            </b-col>
            <b-col sm="12" md="12">
              <div class="mb-3">
                <label class="required">{{ $t('workOrder.sop.deviceType') }}</label>
                <multiselect
                  v-model="checklistDetail.deviceTypes"
                  @input="handleChecklistDetailDeviceTypeSelection"
                  :options="checklistDetailDeviceTypeOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :hide-selected="true"
                  :allow-empty="true"
                  placeholder="Select checklist detail device types"
                  label="deviceName"
                  track-by="deviceCode"
                  :class="{
                    'is-invalid': $v.checklistDetail.deviceTypes.$error,
                  }"
                />
                <div v-if="$v.checklistDetail.deviceTypes.$error" class="invalid-feedback mt-0">
                  <span v-if="!$v.checklistDetail.deviceTypes.required">
                    {{ $t('workOrder.sop.deviceType') }} Wajib Terisi
                  </span>
                </div>
              </div>
            </b-col>
            <b-col sm="12">
              <div class="mb-3">
                <div class="form-check-right form-switch">
                  <input
                    id="SwitchCheckSizemd"
                    v-model="checklistDetail.isMandatory"
                    class="form-check-input ms-2"
                    type="checkbox"
                  />
                  <label class="" for="SwitchCheckSizemd">
                    {{ $t('workOrder.sop.isMandatory') }}
                  </label>
                </div>
              </div>
            </b-col>
            <b-col sm="12">
              <div class="mb-3">
                <div class="form-check-right form-switch">
                  <input
                    id="SwitchDetailValidation"
                    v-model="checklistDetail.is_validation_required"
                    class="form-check-input ms-2"
                    type="checkbox"
                  />
                  <label for="SwitchDetailValidation">
                    {{ $t('workOrder.sop.isValidationRequired') }}
                  </label>
                </div>
              </div>
            </b-col>
            <b-col sm="12" md="6">
              <FormulateInput
                v-model="checklistDetail.timeout_off"
                type="number"
                name="timeout_off"
                label="Timeout Off (s)"
                placeholder="10"
              />
            </b-col>
            <b-col sm="12" md="6">
              <FormulateInput
                v-model="checklistDetail.timeout_on"
                type="number"
                name="timeout_on"
                label="Timeout On (s)"
                placeholder="35"
              />
            </b-col>
            <b-col sm="12" md="6">
              <FormulateInput
                v-model="checklistDetail.time_ingestion"
                type="number"
                name="time_ingestion"
                label="Time Ingestion (s)"
                placeholder="3"
              />
            </b-col>
            <b-col sm="12" md="6">
              <FormulateInput
                v-model="checklistDetail.minimal_data_ingestion_off"
                type="number"
                name="minimal_data_ingestion_off"
                label="Min. Data Ingestion Off"
                placeholder="3"
              />
            </b-col>
            <b-col sm="12" md="6">
              <FormulateInput
                v-model="checklistDetail.minimal_data_ingestion_on"
                type="number"
                name="minimal_data_ingestion_on"
                label="Min. Data Ingestion On"
                placeholder="10"
              />
            </b-col>
            <b-col sm="12" md="6">
              <FormulateInput
                v-model="checklistDetail.minimal_data_validation"
                type="number"
                name="minimal_data_validation"
                label="Min. Data Validation"
                placeholder="10"
              />
            </b-col>
            <b-col sm="12" md="6">
              <FormulateInput
                v-model="checklistDetail.difference_validation"
                type="number"
                step="any"
                name="difference_validation"
                label="Difference Validation"
                placeholder="5"
              />
            </b-col>
            <b-col sm="12" md="6">
              <FormulateInput
                v-model="checklistDetail.difference_n_validation"
                type="number"
                name="difference_n_validation"
                label="Difference N Validation"
                placeholder="3"
              />
            </b-col>
            <b-col sm="12" md="12">
              <FormulateInput
                v-model="checklistDetail.field_qc"
                name="field_qc"
                label="Field QC"
                placeholder="contoh: satellite"
              />
            </b-col>
            <b-col v-if="isSopReferenceActive" sm="12" md="12">
              <div class="mb-3">
                <label>Sop Reference</label>
                <div class="d-flex align-items-center gap-2 mb-3">
                  <b-button variant="outline-primary" @click="$refs.detailSopFileInput.click()">
                    <i class="icon-fill_plus" />
                  </b-button>
                  <span>{{
                    checklistDetail.sop_reference
                      ? checklistDetail.sop_reference.name
                        ? checklistDetail.sop_reference.name
                        : 'Change'
                      : 'No file selected'
                  }}</span>
                  <input
                    ref="detailSopFileInput"
                    type="file"
                    class="d-none"
                    @change="handleSopChecklistFileChange"
                  />
                </div>
                <div>
                  <b-button
                    size="sm"
                    :variant="`${
                      checklistDetail.sop_reference ? 'primary' : 'outline-light'
                    } small`"
                    :disabled="!checklistDetail.sop_reference"
                    @click="showSopReference(checklistDetail)"
                  >
                    <i class="mc-fa icon-outline_eye"></i>
                    View
                  </b-button>
                  <b-button
                    class="m-1"
                    size="sm"
                    :variant="`${
                      checklistDetail.sop_reference ? 'outline-danger' : 'outline-light'
                    } small`"
                    :disabled="!checklistDetail.sop_reference"
                    @click="
                      () => {
                        checklistDetail.sop_reference = null
                      }
                    "
                  >
                    Delete
                  </b-button>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-card>
      </FormulateForm>
      <!-- Custom footer to handle different permissions -->
      <template #modal-footer="{ cancel }">
        <b-row class="mc-modal__filter-footer">
          <b-col sm="12" md="12" class="mc-modal__filter-footer-fav"> </b-col>
          <b-col sm="12" md="12" class="mc-modal__filter-footer-btn">
            <b-button variant="outline-primary" @click="cancel" class="me-3">
              {{ $t('global.cancel') }}
            </b-button>

            <!-- Show save button only if creating new SOP -->
            <b-button
              v-if="!checklistDetail.checklistDetailId && hasAccessAll(['sms-sop:create'])"
              type="submit"
              variant="primary"
              @click="validateAndSubmit"
            >
              {{ $t('global.save') }}
            </b-button>

            <!-- Show update button only if editing existing SOP -->
            <b-button
              v-if="checklistDetail.checklistDetailId && hasAccessAll(['sms-sop:update'])"
              type="submit"
              variant="primary"
              @click="validateAndSubmit"
            >
              {{ $t('global.save') }}
            </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>

    <b-modal
      id="modal-sop-preview"
      centered
      title="SOP Reference"
      hideHeaderClose
      okOnly
      okTitle="Close"
    >
      <div class="w-100 text-center">
        <img v-if="previewImgSrc" :src="previewImgSrc" alt="SOP Reference" class="img-fluid" />
        <div v-else class="text-muted">No preview available</div>
      </div>
    </b-modal>

    <confirmation v-if="modalConfirm" :modal.sync="modalConfirm" @confirmed="toDelete" />
  </Layout>
</template>

<style scoped>
.enum-draggable-container {
  margin-top: 8px;
  padding: 10px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}

.enum-tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.enum-tag-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #6c757d;
  color: white;
  border-radius: 4px;
  font-size: 14px;
  cursor: move;
  transition: all 0.2s ease;
}

.enum-tag-item:hover {
  background: #6c757d;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.drag-handle-enum {
  cursor: grab;
  opacity: 0.7;
  font-size: 12px;
}

.drag-handle-enum:active {
  cursor: grabbing;
}

.drag-handle-enum:hover {
  opacity: 1;
}

.enum-tag-text {
  user-select: none;
}

.enum-tag-remove {
  cursor: pointer;
  font-size: 18px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.enum-tag-remove:hover {
  opacity: 1;
}

.ghost-tag {
  opacity: 0.4;
  background: #cbd5e0;
}

.cursor-move {
  cursor: move;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
